import { useInsertionEffect, useRef } from 'react'

type AnyCallback = (...args: never[]) => unknown

type CallbackBag<T> = { [K in keyof T]: AnyCallback | undefined }

/**
 * Returns an object with the same keys as `callbacks` whose functions never change identity and
 * always call the latest handler passed in.
 *
 * Use it for a set of handlers handed to a memoized component or a context: the consumer gets
 * stable references, so a parent re-creating its handlers on every render doesn't re-render it.
 * The latest handlers are swapped in during the commit (an insertion effect), so a call made from
 * an event handler or an effect always reaches the current one. Don't call these during render.
 *
 * An `undefined` entry stays `undefined` (consumers that branch on "is a handler provided" keep
 * working); the returned object changes identity only when an entry flips between provided and
 * `undefined`.
 */
export function useStableCallbacks<T extends CallbackBag<T>>(callbacks: T): T {
  const latestRef = useRef(callbacks)
  const wrappersRef = useRef<Partial<Record<keyof T, AnyCallback>>>({})
  const resultRef = useRef<{ signature: string; value: T } | null>(null)

  useInsertionEffect(() => {
    latestRef.current = callbacks
  })

  const keys = Object.keys(callbacks) as (keyof T)[]
  const signature = keys
    .map((key) => `${callbacks[key] === undefined ? '-' : '+'}${String(key)}`)
    .join('|')

  if (resultRef.current === null || resultRef.current.signature !== signature) {
    const value: Partial<Record<keyof T, AnyCallback | undefined>> = {}

    for (const key of keys) {
      if (callbacks[key] === undefined) {
        value[key] = undefined
        continue
      }

      if (wrappersRef.current[key] === undefined) {
        wrappersRef.current[key] = (...args: never[]) => {
          const handler = latestRef.current[key] as unknown as
            ((...a: never[]) => unknown) | undefined

          return handler?.(...args)
        }
      }

      value[key] = wrappersRef.current[key]
    }

    resultRef.current = { signature, value: value as T }
  }

  return resultRef.current.value
}
